import { useCallback, useEffect, useRef } from 'react';

const INTERACTIVE_TARGETS = 'input, select, textarea, button, a[href], [contenteditable="true"]';

function isInteractiveTarget(target) {
    return typeof Element !== 'undefined' && target instanceof Element
        && Boolean(target.closest(INTERACTIVE_TARGETS));
}

function hasTextSelection() {
    return typeof window !== 'undefined' && Boolean(window.getSelection?.()?.toString());
}

export default function useDisclosureCardClick({ isOpen = false, onOpen, onClose, openedAt = 0 }) {
    const pointerSequence = useRef(null);
    const headerRef = useRef(null);
    useEffect(() => {
        if (isOpen && openedAt) headerRef.current?.focus({ preventScroll: true });
    }, [isOpen, openedAt]);
    const onPointerDownCapture = useCallback((event) => {
        const now = Date.now();
        if (!pointerSequence.current || now - pointerSequence.current.at > 500) {
            pointerSequence.current = {
                at: now,
                hadSelection: hasTextSelection(),
                interactive: isInteractiveTarget(event.target),
            };
        }
    }, []);

    const onClick = useCallback((event) => {
        if (!isOpen && onOpen && !isInteractiveTarget(event.target)) onOpen();
    }, [isOpen, onOpen]);

    const onDoubleClick = useCallback((event) => {
        event.stopPropagation();
        const sequence = pointerSequence.current;
        pointerSequence.current = null;
        if (!isOpen || !onClose || Date.now() - openedAt < 500
            || isInteractiveTarget(event.target) || sequence?.interactive || sequence?.hadSelection) return;
        window.getSelection?.()?.removeAllRanges();
        onClose();
    }, [isOpen, onClose, openedAt]);

    const onKeyDown = useCallback((event) => {
        if (event.key === 'Escape' && isOpen && onClose) {
            event.preventDefault();
            onClose();
            return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && !isOpen && onOpen
            && event.target === event.currentTarget) {
            event.preventDefault();
            onOpen();
        }
    }, [isOpen, onClose, onOpen]);

    return { onClick, onDoubleClick, onKeyDown, onPointerDownCapture, headerRef };
}
